import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button } from '../../UI/Buttons';
import { Text } from '../../UI/Typography';
import { Input } from '../../UI/Inputs';
import { Modal, Overlay } from '../../UI/Containers';
import { getAllSites } from '../../../Services/Api/attendanceService';

// Define location types
export const LocationType = {
  SITE: 'Site',
  WFH: 'WFH',
  MEETING: 'Meeting'
};

// Styled Components
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const AttendanceCard = styled.div`
  padding: 0;
`;

const TimeContainer = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ClockText = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSizes.xxxl || '3rem'};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const TimerContainer = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ variant, theme }) => 
    variant === 'active' 
      ? `${theme.colors.primary}15` 
      : variant === 'completed' 
        ? `${theme.colors.success || '#4CAF50'}15` 
        : theme.colors.background
  };
  border-color: ${({ variant, theme }) => 
    variant === 'active' 
      ? `${theme.colors.primary}30` 
      : variant === 'completed' 
        ? `${theme.colors.success || '#4CAF50'}30` 
        : theme.colors.border
  };
`;

const TimerValue = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSizes.xxl || '2rem'};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ variant, theme }) => 
    variant === 'active' 
      ? theme.colors.primary 
      : variant === 'completed' 
        ? theme.colors.success || '#4CAF50' 
        : theme.colors.text.primary
  };
  margin: 0;
`;

const LocationTypeContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const LocationTypeOptions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const LocationTypeOption = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ selected, theme }) => 
    selected ? theme.colors.primary : theme.colors.border
  };
  background-color: ${({ selected, theme }) => 
    selected ? `${theme.colors.primary}15` : theme.colors.background
  };
  color: ${({ selected, theme }) => 
    selected ? theme.colors.primary : theme.colors.text.primary
  };
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  font-weight: ${({ selected, theme }) => 
    selected ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.regular
  };

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => `${theme.colors.primary}10`};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 600px) {
    flex: none;
  }
`;

const SiteSelectContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SiteDropdown = styled.div`
  position: relative;
`;

const SiteDropdownButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.loading {
    justify-content: center;
  }
`;

const DropdownIcon = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SiteDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SiteOption = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  background-color: ${({ selected, theme }) => 
    selected ? `${theme.colors.primary}15` : 'transparent'
  };
  color: ${({ selected, theme }) => 
    selected ? theme.colors.primary : theme.colors.text.primary
  };
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: ${({ selected, theme }) => 
    selected ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.regular
  };

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const MeetingInputContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatusContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const StatusItem = styled.div`
  text-align: center;
  flex: 1;
`;

const StatusTime = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const StatusDivider = styled.div`
  width: 1px;
  background-color: ${({ theme }) => theme.colors.border};
  margin: 0 ${({ theme }) => theme.spacing.md};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AttendanceButton = styled(Button)`
  flex: 1;
  min-height: 48px;
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AttendanceModal = ({ 
  isOpen,
  onClose,
  onCheckIn, 
  onCheckOut,
  attendanceData,
  onRefresh
}) => {
  // State management
  const [currentTime, setCurrentTime] = useState(new Date());
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [selectedLocationType, setSelectedLocationType] = useState(LocationType.SITE);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkInLocationType, setCheckInLocationType] = useState(null);
  const [checkOutLocationType, setCheckOutLocationType] = useState(null);
  const [selectedSite, setSelectedSite] = useState('');
  const [isSiteDropdownVisible, setIsSiteDropdownVisible] = useState(false);
  const [meetingLocation, setMeetingLocation] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  // Fetch site locations from real API
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        console.log('Fetching sites from API...');
        const sitesData = await getAllSites();
        
        if (Array.isArray(sitesData) && sitesData.length > 0) {
          setSites(sitesData);
          
          if (!selectedSite && sitesData.length > 0) {
            setSelectedSite(sitesData[0].name);
          }
        } else {
          console.warn('[AttendanceModal] No sites returned from API, using defaults');
          setSites([
            { id: 1, name: 'Main Office' },
            { id: 2, name: 'Branch Office' }
          ]);
          setSelectedSite('Main Office');
        }
      } catch (error) {
        console.error('[AttendanceModal] Error fetching sites:', error);
        setSites([
          { id: 1, name: 'Main Office' },
          { id: 2, name: 'Branch Office' }
        ]);
        setSelectedSite('Main Office');
      } finally {
        setLoadingSites(false);
      }
    };
    
    if (isOpen) {
      fetchSites();
    }
  }, [isOpen, selectedSite]);

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Active timer updates
  useEffect(() => {
    let intervalId;
    
    if (timerActive && checkInTime && !checkOutTime) {
      intervalId = setInterval(() => {
        const now = new Date();
        const diff = now.getTime() - checkInTime.getTime();
        setElapsedTime(diff);
      }, 1000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [timerActive, checkInTime, checkOutTime]);

  // Initialize the component with attendance data
  useEffect(() => {
    if (attendanceData) {
      if (attendanceData.in_time) {
        const today = new Date();
        const [hours, minutes, seconds] = attendanceData.in_time.split(':').map(Number);
        
        const checkInDate = new Date(today);
        checkInDate.setHours(hours, minutes, seconds);
        
        setCheckInTime(checkInDate);
        setCheckInLocationType(attendanceData.in_location_type || LocationType.SITE);
        setTimerActive(attendanceData.out_time === null);
        
        if (attendanceData.in_location_type) {
          if (attendanceData.in_location_type === LocationType.SITE ||
              attendanceData.in_location_type === LocationType.WFH || 
              attendanceData.in_location_type === LocationType.MEETING) {
            setSelectedLocationType(attendanceData.in_location_type);
          } else {
            setSelectedLocationType(LocationType.SITE);
          }

          if (attendanceData.in_location_type === LocationType.SITE && attendanceData.in_location) {
            setSelectedSite(attendanceData.in_location);
          }
          
          if (attendanceData.in_location_type === LocationType.MEETING && attendanceData.in_location) {
            setMeetingLocation(attendanceData.in_location);
          }
        }
      }
      
      if (attendanceData.out_time) {
        const today = new Date();
        const [hours, minutes, seconds] = attendanceData.out_time.split(':').map(Number);
        
        const checkOutDate = new Date(today);
        checkOutDate.setHours(hours, minutes, seconds);
        
        setCheckOutTime(checkOutDate);
        setCheckOutLocationType(attendanceData.out_location_type || null);
        setTimerActive(false);
      }
    }
  }, [attendanceData]);
  
  // Helper functions
  const formatTime = useCallback((date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);
  
  const formatDate = useCallback((date) => {
    return date.toLocaleDateString([], { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }, []);
  
  const formatElapsedTime = useCallback((ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':');
  }, []);
  
  const refreshModal = useCallback(() => {
    setCurrentTime(new Date());
    
    // Only reset elapsed time if we're not currently checked in
    if (!checkInTime || checkOutTime) {
      setElapsedTime(0);
    }
    
    // Re-fetch sites if needed
    if (selectedLocationType === LocationType.SITE) {
      const fetchSites = async () => {
        setLoadingSites(true);
        try {
          const sitesData = await getAllSites();
          if (Array.isArray(sitesData) && sitesData.length > 0) {
            setSites(sitesData);
          }
        } catch (error) {
          console.error('Error refreshing sites:', error);
        } finally {
          setLoadingSites(false);
        }
      };
      fetchSites();
    }
  }, [selectedLocationType, checkInTime, checkOutTime]);

  // Direct attendance handling without location detection
  const handleCheckIn = useCallback(async () => {
    if (selectedLocationType === LocationType.SITE && !selectedSite) {
      alert('Please select a site.');
      return;
    }

    if (selectedLocationType === LocationType.MEETING && !meetingLocation.trim()) {
      alert('Please enter a meeting location.');
      return;
    }

    setIsLoading(true);
    setIsCheckingIn(true);
    
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser.');
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            const location = {
              coords: { latitude, longitude },
              address: `Location: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
            };

            const siteInfo = selectedLocationType === LocationType.SITE ? selectedSite : undefined;
            const meetingInfo = selectedLocationType === LocationType.MEETING ? meetingLocation.trim() : undefined;
            
            const now = new Date();
            setCheckInTime(now);
            setCheckInLocationType(selectedLocationType);
            setTimerActive(true);
            
            // Call the parent's onCheckIn function
            await onCheckIn(selectedLocationType, location, siteInfo, meetingInfo);
            
            // Refresh modal after successful check-in
            refreshModal();
            
            // Call parent refresh if provided
            if (onRefresh) {
              onRefresh();
            }
            
            // Reset loading states
            setIsLoading(false);
            setIsCheckingIn(false);
            
          } catch (error) {
            // Reset check-in states on error
            setCheckInTime(null);
            setCheckInLocationType(null);
            setTimerActive(false);
            setIsLoading(false);
            setIsCheckingIn(false);
            
            console.error('Check-in error:', error);
            alert('Check-in Error: ' + error.message);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          let errorMessage = 'Unable to detect your current location.';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please allow location access and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while accessing location.';
              break;
          }
          
          alert(errorMessage);
          setIsLoading(false);
          setIsCheckingIn(false);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 300000
        }
      );
    } catch (error) {
      console.error('Check-in error:', error);
      alert('Check-in Error: ' + error.message);
      setIsLoading(false);
      setIsCheckingIn(false);
    }
  }, [selectedLocationType, selectedSite, meetingLocation, onCheckIn, refreshModal, onRefresh]);

  const handleCheckOut = useCallback(async () => {
    if (selectedLocationType === LocationType.SITE && !selectedSite) {
      alert('Please select a site.');
      return;
    }

    if (selectedLocationType === LocationType.MEETING && !meetingLocation.trim()) {
      alert('Please enter a meeting location.');
      return;
    }

    setIsLoading(true);
    setIsCheckingIn(false);
    
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser.');
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            const location = {
              coords: { latitude, longitude },
              address: `Location: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
            };

            const siteInfo = selectedLocationType === LocationType.SITE ? selectedSite : undefined;
            const meetingInfo = selectedLocationType === LocationType.MEETING ? meetingLocation.trim() : undefined;
            
            const now = new Date();
            setCheckOutTime(now);
            setCheckOutLocationType(selectedLocationType);
            setTimerActive(false);
            
            // Call the parent's onCheckOut function
            await onCheckOut(selectedLocationType, location, siteInfo, meetingInfo);
            
            // Refresh modal after successful check-out
            refreshModal();
            
            // Call parent refresh if provided
            if (onRefresh) {
              onRefresh();
            }
            
            // Reset loading states
            setIsLoading(false);
            
          } catch (error) {
            // Reset check-out states on error
            setCheckOutTime(null);
            setCheckOutLocationType(null);
            setTimerActive(true);
            setIsLoading(false);
            
            console.error('Check-out error:', error);
            alert('Check-out Error: ' + error.message);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          let errorMessage = 'Unable to detect your current location.';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please allow location access and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while accessing location.';
              break;
          }
          
          alert(errorMessage);
          setIsLoading(false);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 300000
        }
      );
    } catch (error) {
      console.error('Check-out error:', error);
      alert('Check-out Error: ' + error.message);
      setIsLoading(false);
    }
  }, [selectedLocationType, selectedSite, meetingLocation, onCheckOut, refreshModal, onRefresh]);

  const handleModalClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleModalClose();
    }
  };

  // Derived state
  const isCheckedIn = checkInTime !== null;
  const isCheckedOut = checkOutTime !== null;
  const canCheckOut = isCheckedIn && !isCheckedOut;
  const locationOptions = [LocationType.SITE, LocationType.WFH, LocationType.MEETING];

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal size="medium" maxWidth="500px">
        <ModalHeader>
          <Text size="lg" weight="semibold" style={{ margin: 0 }}>
            Mark Attendance
          </Text>
          <CloseButton onClick={handleModalClose}>×</CloseButton>
        </ModalHeader>
        
        <AttendanceCard>
          <TimeContainer>
            <Text size="md" style={{ color: '#6b7280', marginBottom: '8px' }}>
              {formatDate(currentTime)}
            </Text>
            <ClockText>{formatTime(currentTime)}</ClockText>
          </TimeContainer>
          
          {checkInTime && !checkOutTime && (
            <TimerContainer variant="active">
              <Text size="sm" style={{ color: '#6b7280', marginBottom: '8px' }}>
                Time since check-in:
              </Text>
              <TimerValue variant="active">
                {formatElapsedTime(elapsedTime)}
              </TimerValue>
            </TimerContainer>
          )}
          
          {checkInTime && checkOutTime && (
            <TimerContainer variant="completed">
              <Text size="sm" style={{ color: '#6b7280', marginBottom: '8px' }}>
                Total work duration:
              </Text>
              <TimerValue variant="completed">
                {formatElapsedTime(checkOutTime.getTime() - checkInTime.getTime())}
              </TimerValue>
            </TimerContainer>
          )}

          {/* Only show location selection if check-out is not completed */}
          {!(checkInTime && checkOutTime) && (
            <>
              <LocationTypeContainer>
                <Text size="sm" weight="semibold" style={{ color: '#6b7280', marginBottom: '12px' }}>
                  Select Location Type:
                </Text>
                <LocationTypeOptions>
                  {locationOptions.map((locationType) => (
                    <LocationTypeOption
                      key={locationType}
                      selected={selectedLocationType === locationType}
                      onClick={() => setSelectedLocationType(locationType)}
                      disabled={isLoading}
                    >
                      {locationType}
                    </LocationTypeOption>
                  ))}
                </LocationTypeOptions>
              </LocationTypeContainer>
              
              {selectedLocationType === LocationType.SITE && (
                <SiteSelectContainer>
                  <Text size="sm" weight="semibold" style={{ color: '#6b7280', marginBottom: '8px' }}>
                    Select Site:
                  </Text>
                  {loadingSites ? (
                    <SiteDropdownButton className="loading" disabled>
                      <LoadingSpinner />
                    </SiteDropdownButton>
                  ) : (
                    <SiteDropdown>
                      <SiteDropdownButton
                        onClick={() => setIsSiteDropdownVisible(!isSiteDropdownVisible)}
                        disabled={sites.length === 0}
                      >
                        <span>{selectedSite || 'Select a site'}</span>
                        <DropdownIcon>▼</DropdownIcon>
                      </SiteDropdownButton>
                      
                      {isSiteDropdownVisible && (
                        <SiteDropdownMenu>
                          {sites.map((site) => (
                            <SiteOption
                              key={site.id}
                              selected={selectedSite === site.name}
                              onClick={() => {
                                setSelectedSite(site.name);
                                setIsSiteDropdownVisible(false);
                              }}
                            >
                              {site.name}
                            </SiteOption>
                          ))}
                        </SiteDropdownMenu>
                      )}
                    </SiteDropdown>
                  )}
                </SiteSelectContainer>
              )}
              
              {selectedLocationType === LocationType.MEETING && (
                <MeetingInputContainer>
                  <Text size="sm" weight="semibold" style={{ color: '#6b7280', marginBottom: '8px' }}>
                    Enter Meeting Location:
                  </Text>
                  <Input
                    type="text"
                    value={meetingLocation}
                    onChange={(e) => setMeetingLocation(e.target.value)}
                    placeholder="Enter meeting location"
                  />
                </MeetingInputContainer>
              )}
            </>
          )}
          
          <StatusContainer>
            <StatusItem>
              <Text size="sm" style={{ color: '#6b7280', marginBottom: '8px' }}>
                Check In
              </Text>
              <StatusTime>
                {checkInTime ? formatTime(checkInTime) : '--:--'}
              </StatusTime>
              {checkInLocationType && (
                <Text size="xs" style={{ color: '#6b7280', margin: 0 }}>
                  {checkInLocationType}
                </Text>
              )}
            </StatusItem>
            
            <StatusDivider />
            
            <StatusItem>
              <Text size="sm" style={{ color: '#6b7280', marginBottom: '8px' }}>
                Check Out
              </Text>
              <StatusTime>
                {checkOutTime ? formatTime(checkOutTime) : '--:--'}
              </StatusTime>
              {checkOutLocationType && (
                <Text size="xs" style={{ color: '#6b7280', margin: 0 }}>
                  {checkOutLocationType}
                </Text>
              )}
            </StatusItem>
          </StatusContainer>
          
          <ButtonContainer>
            <AttendanceButton 
              variant="primary"
              onClick={handleCheckIn}
              disabled={isCheckedIn || isLoading}
            >
              {isLoading && isCheckingIn ? (
                <LoadingSpinner />
              ) : (
                'Check-In'
              )}
            </AttendanceButton>
            
            <AttendanceButton 
              variant="danger"
              onClick={handleCheckOut}
              disabled={!canCheckOut || isLoading}
            >
              {isLoading && !isCheckingIn ? (
                <LoadingSpinner />
              ) : (
                'Check-Out'
              )}
            </AttendanceButton>
          </ButtonContainer>
        </AttendanceCard>
      </Modal>
    </Overlay>
  );
};

export default AttendanceModal;